import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function HowItWorksPage() {
  const title = "How Bellhopt Works | Grocery Delivery, Stocking, Concierge Layer";
  const description =
    "See how Bellhopt works for guests, hosts, and property managers. Bellhopt Concierge delivers, stocks, and organizes. 24-hour lead time required.";
  const canonical = "https://bellhopt.com/how-it-works/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "How Bellhopt Works",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "How It Works", item: canonical },
      ],
    },
  ];

  return (
    <div className="w-full min-h-screen bg-[#F9FAFB] pt-[72px] font-segoe">
      <SeoHead
        title={title}
        description={description}
        canonical="/how-it-works/"
        robots="index,follow"
        ogTitle="How Bellhopt Works"
        ogDescription="A simple flow for guests, and a scalable execution layer for operators. Delivered, stocked, organized by Bellhopt Concierge."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto py-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            How Bellhopt Works
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Bellhopt combines a guest-friendly ordering flow with a concierge execution layer that delivers, stocks, and organizes homes to a consistent arrival-ready standard.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
            <Link to="/for/property-managers" className={SECONDARY_CTA}>For Property Managers</Link>
          </div>
          <p className="mt-4 text-[13px] text-[#6B7280]">
            24-hour lead time required. Built for any size portfolio, including 1,000+ homes.
          </p>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="two-things" title="Two Things Bellhopt Solves">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Guests want convenience</strong>
                <p>They want the stay to feel comfortable from the first hour.</p>
              </li>
              <li>
                <strong>Operators need consistency</strong>
                <p>They need execution that works across properties, access types, and markets without adding internal workload.</p>
              </li>
            </ol>
          </Section>

          <Section id="for-hosts" title="For Hosts And Property Managers">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Onboard your properties</strong>
                <p>Onboard via supported integration, or manual onboarding if needed. Keep property details up to date so our operations can dispatch reliably.</p>
              </li>
              <li>
                <strong>Share access and property instructions</strong>
                <p>Access method, arrival rules, substitution preferences, and notes that affect execution.</p>
              </li>
              <li>
                <strong>Launch the guest flow</strong>
                <p>Guests receive access to 20,000+ products through a shopping experience aligned to the stay.</p>
              </li>
              <li>
                <strong>Bellhopt Concierge executes</strong>
                <p>Shop, deliver, stock, and organize based on the order and property instructions.</p>
              </li>
              <li>
                <strong>Maintain a consistent standard</strong>
                <p>Repeatable execution that reduces back-and-forth and improves arrival experience.</p>
              </li>
            </ol>
          </Section>

          <Section id="for-guests" title="For Guests">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Shop before arrival</strong>
                <p>Browse 20,000+ products and order exactly what you want through a pre-arrival shopping experience.</p>
              </li>
              <li>
                <strong>Confirm timing</strong>
                <p>Orders require at least 24 hours lead time.</p>
              </li>
              <li>
                <strong>Arrival-ready experience</strong>
                <p>Bellhopt Concierge delivers, stocks, and organizes so your first hour feels effortless.</p>
              </li>
            </ol>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Make Arrival Effortless
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              A simple flow for guests, and a scalable execution layer for operators.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
              <Link to="/for/property-managers" className={SECONDARY_CTA}>For Property Managers</Link>
            </div>
          </section>

          <Section id="stocking-standards" title="Stocking And Organization Standards">
            <p>Bellhopt Concierge focuses on:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>placing items where they belong</li>
              <li>basic organization that looks clean and intentional</li>
              <li>reducing clutter and improving first impression</li>
            </ul>
            <p className="mt-4">This is not just a bag drop. It is arrival readiness.</p>
          </Section>

          <Section id="policies" title="Policies That Protect Quality">
            <ul className="list-disc pl-6">
              <li>24-hour lead time required</li>
              <li>Clear access protocols</li>
              <li>Substitution preferences where available</li>
              <li>Communication standards that reduce confusion</li>
            </ul>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/pre-arrival-groceries">Pre-arrival groceries</Link></li>
              <li><Link className="text-primary" to="/vacation-rental-grocery-delivery">Vacation rental grocery delivery</Link></li>
              <li><Link className="text-primary" to="/airbnb-grocery-stocking">Airbnb grocery stocking</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}

export default HowItWorksPage;
